import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalService } from 'src/app/components/core/_infrastructure/classes/services/global.service';
import { FilterSearchingBaseViewModel, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';
import { PeriodoCamService } from '../periodocam.service';
import { FilterSearchingPeriodoCamViewModel, PeriodoCamItemResultsViewModel } from '../periodocam.types';
import { EnumCodigosTwTcAcciones } from '../../../../../_importacion/solicitudes/types/enum';
import { ConfirmationService } from 'primeng/api';
import { equalObjects } from '../../../../../_campanias/types/solicitudcam-datosbasicos.types';
import { catchError, of, tap } from 'rxjs';

@Component({
    selector: 'app-periodo-wrapper',
    templateUrl: './periodo-wrapper.component.html',
})
export class PeriodoCamWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinner = false;
    searching = false;
    result: PeriodoCamItemResultsViewModel | null;
    payload?: FilterSearchingPeriodoCamViewModel;

    campanias: Tc[];
    habilitarNuevo = true;

    private route = '/mantenimiento/periodos/datosbasicos';

    constructor(
        protected notificacionService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private globalService: GlobalService,
        private periodoCamService: PeriodoCamService,
        private confirmationService: ConfirmationService,
        private router: Router,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificacionService, exceptionHandler);
    }

    ngOnInit(): void {
        this.getCampanias();
        this.recoverLastSearch();
        this.habilitarNuevo = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.NUEVO_PERIODO).Visible;
    }

    /**
     * We try to get back the previous search done by the user.
     */
    private recoverLastSearch() {
        this.payload = this.periodoCamService.getLastSearch();
        if (!this.payload) {
            return;
        }
    }

    private getCampanias(): void {
        this.globalService.getAllCampanias().subscribe((campanias: Tc[]) => {
            this.campanias = campanias;
        });
    }

    /**
     * Respond to the action of searching on variedad-filter.
     * It will send the filter to the backend and set the response.
     * @param payload FilterSearchingPeriodoCamViewModel
     * @return void
     */
    onSearch(payload: FilterSearchingPeriodoCamViewModel): void {
        this.navMenuService.clearMenuServiceForPeriodo();
        if (!equalObjects(this.payload, payload)) {
            // As soon as the user changes the "filter", the previous payload pagination values have no sense
            // When recovering the request params from the last result, the pagination params won't be reset.
            payload = this.clearPaginationFilter(payload);
        }

        this.payload = { ...payload } as FilterSearchingPeriodoCamViewModel; // To keep a copy to be used in the lazy search (paginator)
        if (!payload) {
            return;
        }

        this.searching = true;
        this.result = null;

        this.periodoCamService
            .getList(this.payload)
            .pipe(
                tap(results => {
                    this.result = results as PeriodoCamItemResultsViewModel;
                    this.searching = true;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of([]);
                }),
            )
            .subscribe();
    }

    /**
     * We don't clear the whole filter but only the fields related to the pagination.
     */
    private clearPaginationFilter(payload: FilterSearchingPeriodoCamViewModel): FilterSearchingPeriodoCamViewModel {
        if (!payload) {
            return payload;
        }
        payload.PageNumber = 0;
        payload.Asc = 0;
        payload.OrderBy = '';
        return payload;
    }

    /**
     * It will be invoked when using the paginator
     * @param lazyLoadParams FilterSearchingBaseViewModel
     * @return void
     */
    onSearchLazy(lazyLoadParams: FilterSearchingBaseViewModel): void {
        this.payload = {
            ...this.payload,
            PageNumber: lazyLoadParams.PageNumber,
            Asc: lazyLoadParams.Asc,
            OrderBy: lazyLoadParams.OrderBy,
        } as FilterSearchingPeriodoCamViewModel;
        this.onSearch(this.payload);
    }

    /**
     * Invoked when the user wants to clear the form
     * @return void
     */
    onReset(): void {
        this.searching = false;
        this.result = null;
        this.periodoCamService.clearLastSearch();
        this.navMenuService.clearMenuServiceForPeriodo();
    }

    /**
     * Redirigimos al formulario de creación de un nuevo registro
     * @return void
     */
    onAdd(): void {
        // Borramos una posible selección anterior de periodo en el menú
        this.navMenuService.clearMenuServiceForPeriodo();
        this.router.navigate([this.route + '/create']);
    }

    /**
     * Redirigimos al formulario de edición.
     * Cuando el parámetro sea null, el backend captura el error y se muestra en pantalla.
     * @param itemId number|null
     * @return void
     */
    onEdit(itemId: number | null): void {
        this.router.navigate([this.route + '/edit', itemId]);
    }

    onClone(itemId: number): void {
        this.confirmationService.confirm({
            message: '¿Está seguro de clonar este periodo?',
            accept: () => {
                //Actual logic to perform a confirmation
                this.periodoCamService.clonePeriodo(itemId).subscribe({
                    next: res => {
                        this.addSuccessMessage('Periodo clonado correctamente');
                        let idPeriodo = res;
                        this.router.navigate([this.route + '/edit', idPeriodo]);
                        //this.onEdit(idPeriodo);
                    },
                    error: error => {
                        this.catchServerError(error);
                    },
                });
            },
        });
    }

    /**
     * Invoked when the user wants to export the result to excel
     * @return void
     */
    onExportExcel(): void {
        this.visibilitySpinner = true;

        const model: FilterSearchingPeriodoCamViewModel = new FilterSearchingPeriodoCamViewModel();
        Object.assign(model, this.payload);

        this.periodoCamService.export(model).subscribe({
            next: res => {
                this.OpenDownloadedFile(res);
                this.visibilitySpinner = false;
            },
            error: error => {
                this.catchServerError(error);
                this.visibilitySpinner = false;
            },
        });
    }

    /**
     * To check whether there are results to export
     * @returns boolean
     */
    exportIsAvailable(): boolean {
        return this.result?.ResultList ? this.result?.ResultList?.length > 0 : false;
    }
}
